/**
 * What the HUD covers at the bottom of a touch screen (ADR 0043): the spell
 * bar and its gauges, the quick potion and the mount, the chat, folded or
 * open. The world camera reaches that far below the map so its last rows (a
 * cave's way out, SHE-79) can still be seen and touched above them.
 */
const BOTTOM_HUD = ['.spell-bar', '.chat', '.touch-actions'];

/** Screen pixels from the highest of these boxes to the bottom of the screen. */
export function coveredBelow(
  boxes: readonly { top: number; height: number }[],
  screenHeight: number,
): number {
  let covered = 0;
  for (const box of boxes) {
    if (box.height <= 0) continue;
    covered = Math.max(covered, screenHeight - box.top);
  }
  return Math.max(0, Math.min(screenHeight, covered));
}

/** The bottom HUD's height on screen now, in screen pixels. */
export function bottomHudHeight(): number {
  const boxes = BOTTOM_HUD.flatMap((selector) =>
    [...document.querySelectorAll<HTMLElement>(selector)]
      .filter((element) => !element.hidden && element.getClientRects().length > 0)
      .map((element) => element.getBoundingClientRect()),
  );
  return coveredBelow(boxes, window.innerHeight);
}

/**
 * Calls `listener` whenever the bottom HUD changes size (the chat opened, a
 * mount worn). The HUD is built after the scene: elements that appear later
 * are caught by watching the body. Returns the unsubscribe.
 */
export function onBottomHudResize(listener: () => void): () => void {
  const resize = new ResizeObserver(listener);
  const watched = new Set<Element>();
  const watch = (): void => {
    for (const selector of BOTTOM_HUD) {
      for (const element of document.querySelectorAll(selector)) {
        if (watched.has(element)) continue;
        watched.add(element);
        resize.observe(element);
      }
    }
  };
  watch();
  const added = new MutationObserver(watch);
  added.observe(document.body, { childList: true });
  return () => {
    resize.disconnect();
    added.disconnect();
  };
}
